import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, EyeOff, User, Building, Mail, UserCheck, Loader2,
  Users, Briefcase, RefreshCw, ChevronRight, ChevronLeft,
  AlertTriangle, Lock, CheckCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api/apiClient';
import { TWProgressBar, ProgressStep } from '../ui/TWProgressBar';
import { BetaCodeInput } from './BetaCodeInput';
import toast from '../../utils/toast';
import { getImageUrl } from '../../utils/imageUtils';

interface RegisterFormTWProps {
  onSwitchToLogin?: () => void;
  onClose?: () => void;
}

interface PrivateRegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail?: string;
  username: string;
  password: string;
  confirmPassword: string;
  accountType: 'private';
  agreeToTerms: boolean;
  betaCode?: string;
}

interface CompanyRegistrationData {
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail?: string;
  username: string;
  password: string;
  confirmPassword: string;
  accountType: 'company';
  companyName: string;
  address: {
    street: string;
    houseNumber: string;
    zipCode: string;
    city: string;
    country: string;
  };
  agreeToTerms: boolean;
  betaCode?: string;
}

type RegistrationData = PrivateRegistrationData | CompanyRegistrationData;

// Step configurations
const PRIVATE_STEPS = [
  { number: 1, id: 'Schritt 1', name: 'Kontotyp' },
  { number: 2, id: 'Schritt 2', name: 'Persönliche Daten' },
  { number: 3, id: 'Schritt 3', name: 'Sicherheit' }
];

const COMPANY_STEPS = [
  { number: 1, id: 'Schritt 1', name: 'Kontotyp' },
  { number: 2, id: 'Schritt 2', name: 'Firmendaten' },
  { number: 3, id: 'Schritt 3', name: 'Ansprechpartner' },
  { number: 4, id: 'Schritt 4', name: 'Sicherheit' }
];

export const RegisterFormTW: React.FC<RegisterFormTWProps> = ({
  onSwitchToLogin,
  onClose
}) => {
  const { register, isLoading, error, clearError } = useAuth();

  // Form State
  const [currentStep, setCurrentStep] = useState(1);
  const [accountType, setAccountType] = useState<'private' | 'company'>('private');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Animation State
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'exit' | 'enter'>('idle');
  const [slideDirection, setSlideDirection] = useState<'forward' | 'backward'>('forward');
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');
  const contentRef = useRef<HTMLDivElement>(null);

  // Refs for auto-focus
  const privateCardRef = useRef<HTMLButtonElement>(null);

  const [privateData, setPrivateData] = useState<PrivateRegistrationData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    accountType: 'private',
    agreeToTerms: false,
    betaCode: ''
  });

  const [companyData, setCompanyData] = useState<CompanyRegistrationData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    accountType: 'company',
    companyName: '',
    address: {
      street: '',
      houseNumber: '',
      zipCode: '',
      city: '',
      country: 'DE'
    },
    agreeToTerms: false,
    betaCode: ''
  });

  const steps = accountType === 'private' ? PRIVATE_STEPS : COMPANY_STEPS;
  const totalSteps = steps.length;
  const currentData: RegistrationData = accountType === 'private' ? privateData : companyData;

  // Auto-focus on first element when page loads or step changes
  useEffect(() => {
    if (animationPhase !== 'idle') return;

    const timer = setTimeout(() => {
      if (currentStep === 1) {
        privateCardRef.current?.focus();
      } else {
        const firstInput = document.querySelector('[data-autofocus]') as HTMLElement;
        firstInput?.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStep, animationPhase]);

  // Convert steps to progress bar format
  const getProgressSteps = (): ProgressStep[] => {
    return steps.map((step) => ({
      id: step.id,
      name: step.name,
      status: step.number < currentStep
        ? 'complete'
        : step.number === currentStep
          ? 'current'
          : 'upcoming'
    }));
  };

  // Validation
  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (accountType === 'private') {
      switch (step) {
        case 1:
          break;
        case 2:
          if (!privateData.firstName.trim()) errors.firstName = 'Vorname ist erforderlich';
          if (!privateData.lastName.trim()) errors.lastName = 'Nachname ist erforderlich';
          if (privateData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(privateData.email)) {
              errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
            } else if (privateData.email.trim().toLowerCase() !== (privateData.confirmEmail || '').trim().toLowerCase()) {
              errors.confirmEmail = 'E-Mail-Adressen stimmen nicht überein';
            }
          }
          if (!privateData.username.trim()) {
            errors.username = 'Benutzername ist erforderlich';
          } else if (privateData.username.length < 3) {
            errors.username = 'Benutzername muss mindestens 3 Zeichen lang sein';
          }
          break;
        case 3:
          if (!privateData.password) {
            errors.password = 'Passwort ist erforderlich';
          } else if (privateData.password.length < 6) {
            errors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
          }
          if (privateData.password !== privateData.confirmPassword) {
            errors.confirmPassword = 'Passwörter stimmen nicht überein';
          }
          if (!privateData.agreeToTerms) {
            errors.agreeToTerms = 'Sie müssen den Nutzungsbedingungen zustimmen';
          }
          break;
      }
    } else {
      switch (step) {
        case 1:
          break;
        case 2:
          if (!companyData.companyName.trim()) errors.companyName = 'Firmenname ist erforderlich';
          if (!companyData.address.street.trim()) errors.street = 'Straße ist erforderlich';
          if (!companyData.address.houseNumber.trim()) errors.houseNumber = 'Hausnummer ist erforderlich';
          if (!companyData.address.zipCode.trim()) errors.zipCode = 'PLZ ist erforderlich';
          if (!companyData.address.city.trim()) errors.city = 'Stadt ist erforderlich';
          break;
        case 3:
          if (!companyData.firstName.trim()) errors.firstName = 'Vorname ist erforderlich';
          if (!companyData.lastName.trim()) errors.lastName = 'Nachname ist erforderlich';
          if (companyData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(companyData.email)) {
              errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
            } else if (companyData.email.trim().toLowerCase() !== (companyData.confirmEmail || '').trim().toLowerCase()) {
              errors.confirmEmail = 'E-Mail-Adressen stimmen nicht überein';
            }
          }
          if (!companyData.username.trim()) {
            errors.username = 'Benutzername ist erforderlich';
          } else if (companyData.username.length < 3) {
            errors.username = 'Benutzername muss mindestens 3 Zeichen lang sein';
          }
          break;
        case 4:
          if (!companyData.password) {
            errors.password = 'Passwort ist erforderlich';
          } else if (companyData.password.length < 6) {
            errors.password = 'Passwort muss mindestens 6 Zeichen lang sein';
          }
          if (companyData.password !== companyData.confirmPassword) {
            errors.confirmPassword = 'Passwörter stimmen nicht überein';
          }
          if (!companyData.agreeToTerms) {
            errors.agreeToTerms = 'Sie müssen den Nutzungsbedingungen zustimmen';
          }
          break;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isCurrentStepValid = (): boolean => {
    if (accountType === 'private') {
      switch (currentStep) {
        case 1:
          return true;
        case 2:
          const privateEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const privateEmailValid = !privateData.email.trim() || privateEmailRegex.test(privateData.email);
          return (
            privateData.firstName.trim().length > 0 &&
            privateData.lastName.trim().length > 0 &&
            privateEmailValid &&
            privateData.username.trim().length >= 3
          );
        case 3:
          return (
            privateData.password.length >= 6 &&
            privateData.confirmPassword.length >= 6 &&
            privateData.password === privateData.confirmPassword &&
            privateData.agreeToTerms
          );
        default:
          return false;
      }
    } else {
      switch (currentStep) {
        case 1:
          return true;
        case 2:
          return (
            companyData.companyName.trim().length > 0 &&
            companyData.address.street.trim().length > 0 &&
            companyData.address.houseNumber.trim().length > 0 &&
            companyData.address.zipCode.trim().length > 0 &&
            companyData.address.city.trim().length > 0
          );
        case 3:
          const companyEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const companyEmailValid = !companyData.email.trim() || companyEmailRegex.test(companyData.email);
          return (
            companyData.firstName.trim().length > 0 &&
            companyData.lastName.trim().length > 0 &&
            companyEmailValid &&
            companyData.username.trim().length >= 3
          );
        case 4:
          return (
            companyData.password.length >= 6 &&
            companyData.confirmPassword.length >= 6 &&
            companyData.password === companyData.confirmPassword &&
            companyData.agreeToTerms
          );
        default:
          return false;
      }
    }
  };

  // Navigation with animation
  const animateToStep = (targetStep: number, direction: 'forward' | 'backward') => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
    }
    setSlideDirection(direction);
    setAnimationPhase('exit');

    setTimeout(() => {
      setCurrentStep(targetStep);
      setFormErrors({});
      setAnimationPhase('enter');

      requestAnimationFrame(() => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
        requestAnimationFrame(() => {
          setAnimationPhase('idle');
          setTimeout(() => setContentHeight('auto'), 300);
        });
      });
    }, 200);
  };

  const handleNext = () => {
    if (validateStep(currentStep) && currentStep < totalSteps) {
      animateToStep(currentStep + 1, 'forward');
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      animateToStep(currentStep - 1, 'backward');
    }
  };

  const handleStepClick = (stepIndex: number) => {
    const stepNumber = stepIndex + 1;
    if (stepNumber < currentStep) {
      animateToStep(stepNumber, 'backward');
    }
  };

  const handleAccountTypeChange = (type: 'private' | 'company') => {
    setAccountType(type);
    setFormErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateStep(currentStep)) return;

    try {
      const result = await register(currentData);

      if (result.success) {
        setRegistrationSuccess(true);
        if (currentData.email.trim()) {
          toast.success('Registrierung erfolgreich! Bitte bestätige deine E-Mail-Adresse um dich anzumelden.');
        } else {
          toast.success('Registrierung erfolgreich! Du kannst dich jetzt mit deinem Benutzernamen anmelden.');
        }
        setFormErrors({});

        if (onClose) {
          setTimeout(() => onClose(), 4000);
        }
      }
    } catch (registrationError: any) {
      if (registrationError?.response?.data?.action === 'resend_verification') {
        setShowResendButton(true);
        setResendEmail(currentData.email);
        setFormErrors({ general: registrationError.response.data.message });
      } else if (registrationError?.response?.data?.action === 'login') {
        setFormErrors({
          general: registrationError.response.data.message + ' Bitte melden Sie sich stattdessen an.'
        });
      } else if (registrationError?.response?.data?.field === 'username') {
        clearError();
        // Zurück zu Schritt 2 navigieren, damit der Fehler am Username-Feld sichtbar ist
        if (currentStep !== 2) {
          animateToStep(2, 'backward');
          // Fehler erst nach der Animation setzen (animateToStep löscht formErrors nach 200ms)
          setTimeout(() => {
            setFormErrors({
              username: registrationError.response.data.message
            });
          }, 250);
        } else {
          setFormErrors({
            username: registrationError.response.data.message
          });
        }
      } else {
        setFormErrors({
          general: registrationError?.response?.data?.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.'
        });
      }
    }
  };

  const handleResendVerification = async () => {
    if (!resendEmail) return;

    setResendLoading(true);
    try {
      const result = await apiClient.resendVerification(resendEmail);
      if (result.success) {
        toast.success(result.data?.message || 'Bestätigungs-E-Mail wurde erneut gesendet!');
        setShowResendButton(false);
        setFormErrors({});
      } else {
        toast.error(result.error || 'Fehler beim Senden der E-Mail');
      }
    } catch (err) {
      toast.error('Fehler beim Senden der E-Mail. Bitte versuchen Sie es erneut.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean, isAddress = false) => {
    if (accountType === 'private') {
      setPrivateData(prev => ({ ...prev, [field]: value }));
    } else {
      if (isAddress) {
        setCompanyData(prev => ({
          ...prev,
          address: { ...prev.address, [field]: value }
        }));
      } else {
        setCompanyData(prev => ({ ...prev, [field]: value }));
      }
    }

    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Logo-URL - zentrale Funktion für korrekte Umgebungserkennung
  const logoUrl = getImageUrl('/images/branding/renditefuchs-logo.svg');

  const isLastStep = currentStep === totalSteps;
  const isTeal = accountType === 'company';

  // Input styling - dynamisch basierend auf Account-Typ
  const focusColor = isTeal ? 'focus:outline-teal-500' : 'focus:outline-brand';
  const inputBaseClass = "block w-full rounded-md bg-gray-100 dark:bg-white/5 pl-10 pr-3 py-2.5 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 placeholder:text-gray-500 sm:text-sm/6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
  const inputNormalClass = `${inputBaseClass} outline-gray-300 dark:outline-white/10 focus:outline-2 focus:-outline-offset-2 ${focusColor}`;
  const inputErrorClass = `${inputBaseClass} outline-red-500 focus:outline-red-500`;
  const inputNoIconClass = `block w-full rounded-md bg-gray-100 dark:bg-white/5 px-3 py-2.5 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 outline-gray-300 dark:outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 ${focusColor} sm:text-sm/6 disabled:opacity-50 transition-colors`;
  const inputNoIconErrorClass = "block w-full rounded-md bg-gray-100 dark:bg-white/5 px-3 py-2.5 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 outline-red-500 placeholder:text-gray-500 focus:outline-red-500 sm:text-sm/6 disabled:opacity-50 transition-colors";

  // Button styling
  const primaryButtonClass = isTeal
    ? "flex w-full justify-center items-center gap-2 rounded-md bg-teal-500 px-4 py-2.5 text-sm/6 font-semibold text-white hover:bg-teal-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    : "flex w-full justify-center items-center gap-2 rounded-md bg-brand px-4 py-2.5 text-sm/6 font-semibold text-gray-950 hover:bg-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  const secondaryButtonClass = "flex items-center justify-center gap-2 rounded-md bg-gray-100 dark:bg-white/5 px-4 py-2.5 text-sm/6 font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-white/10 hover:bg-gray-200 dark:hover:bg-white/10 hover:text-gray-900 dark:hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400 dark:focus-visible:outline-white/50 disabled:opacity-50 transition-colors";

  // Animation transform
  const getTransform = () => {
    if (animationPhase === 'exit') {
      return slideDirection === 'forward' ? 'translateX(-30px)' : 'translateX(30px)';
    }
    if (animationPhase === 'enter') {
      return slideDirection === 'forward' ? 'translateX(30px)' : 'translateX(-30px)';
    }
    return 'translateX(0)';
  };

  return (
    <div className="flex min-h-screen flex-col justify-center px-6 py-12 lg:px-8">
      {/* Logo & Header */}
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <Link to="/" aria-label="Zur Startseite" className="block">
          <img
            alt="Renditefuchs"
            src={logoUrl}
            className="mx-auto h-10 w-auto"
          />
        </Link>
        <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white">
          Konto erstellen
        </h2>
        <p className="mt-2 text-center text-sm/6 text-gray-600 dark:text-gray-500">
          {accountType === 'private' ? 'Privatkonto' : 'Firmenkonto'} - Schritt {currentStep} von {totalSteps}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        {/* Progress Bar */}
        <div className="mb-6">
          <TWProgressBar
            steps={getProgressSteps()}
            onStepClick={handleStepClick}
            variant={isTeal ? 'teal' : 'gold'}
          />
        </div>

        {/* Form Card */}
        <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.02] p-6">
          <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on" noValidate>
          {/* Success Message */}
          {registrationSuccess && (
            <div className="p-6 rounded-xl bg-status-success/10 border border-green-500/50">
              <div className="flex items-start gap-4">
                <CheckCircle className="h-8 w-8 text-status-success shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Registrierung erfolgreich!</h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                    {currentData.email.trim()
                      ? `Ihr Konto wurde erstellt. Bitte bestätigen Sie Ihre E-Mail-Adresse (${currentData.email}) um sich anzumelden.`
                      : `Ihr Konto wurde erstellt. Sie können sich jetzt mit Ihrem Benutzernamen (${currentData.username}) anmelden.`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Messages */}
          {(error || formErrors.general) && !registrationSuccess && (
            <div className="p-4 rounded-lg bg-status-error/10 border border-red-500/50">
              <p className="text-sm text-red-400">{formErrors.general || error}</p>
              {showResendButton && (
                <div className="mt-3 pt-3 border-t border-red-500/30">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className={secondaryButtonClass}
                  >
                    {resendLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Wird gesendet...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        E-Mail erneut senden
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step Content with Animation */}
          {!registrationSuccess && (
            <div
              className="overflow-hidden -mx-3 px-3 -my-1 py-1"
              style={{
                height: contentHeight === 'auto' ? 'auto' : contentHeight,
                transition: contentHeight !== 'auto' ? 'height 0.3s ease-out' : 'none'
              }}
            >
              <div
                ref={contentRef}
                style={{
                  opacity: animationPhase === 'idle' ? 1 : 0,
                  transform: getTransform(),
                  transition: animationPhase === 'enter' ? 'none' : 'opacity 0.2s ease-out, transform 0.2s ease-out'
                }}
              >
                {/* Step 1: Account Type Selection */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Private Account Card */}
                      <button
                        ref={privateCardRef}
                        type="button"
                        onClick={() => handleAccountTypeChange('private')}
                        tabIndex={1}
                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100 dark:focus-visible:ring-offset-gray-950 ${
                          accountType === 'private'
                            ? 'border-brand bg-brand/10'
                            : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-100 dark:hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${accountType === 'private' ? 'bg-brand/20' : 'bg-gray-100 dark:bg-white/10'}`}>
                            <Users className={`h-5 w-5 ${accountType === 'private' ? 'text-brand' : 'text-gray-500 dark:text-gray-400'}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">Privatperson</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Für private Investoren</p>
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                          Verwalten Sie Ihr persönliches Portfolio und verfolgen Sie Ihre Investments.
                        </p>
                      </button>

                      {/* Company Account Card */}
                      <button
                        type="button"
                        onClick={() => handleAccountTypeChange('company')}
                        tabIndex={2}
                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-100 dark:focus-visible:ring-offset-gray-950 ${
                          accountType === 'company'
                            ? 'border-teal-500 bg-teal-500/10'
                            : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20 hover:bg-gray-100 dark:hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${accountType === 'company' ? 'bg-teal-500/20' : 'bg-gray-100 dark:bg-white/10'}`}>
                            <Briefcase className={`h-5 w-5 ${accountType === 'company' ? 'text-teal-400' : 'text-gray-500 dark:text-gray-400'}`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white">Unternehmen</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Für Firmen & Gewerbetreibende</p>
                          </div>
                        </div>
                        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                          Professionelle Funktionen für Ihr Unternehmen mit Rechnungsstellung.
                        </p>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2 (Private): Personal Data */}
                {currentStep === 2 && accountType === 'private' && (
                  <div className="space-y-5">
                    {/* Name Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="private-firstName" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                          Vorname *
                        </label>
                        <div className="mt-2 relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <input
                            id="private-firstName"
                            type="text"
                            placeholder="Max"
                            value={privateData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            disabled={isLoading}
                            tabIndex={1}
                            data-autofocus
                            className={formErrors.firstName ? inputErrorClass : inputNormalClass}
                          />
                        </div>
                        {formErrors.firstName && <p className="mt-1 text-sm text-red-400">{formErrors.firstName}</p>}
                      </div>

                      <div>
                        <label htmlFor="private-lastName" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                          Nachname *
                        </label>
                        <div className="mt-2 relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <input
                            id="private-lastName"
                            type="text"
                            placeholder="Mustermann"
                            value={privateData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            disabled={isLoading}
                            tabIndex={2}
                            className={formErrors.lastName ? inputErrorClass : inputNormalClass}
                          />
                        </div>
                        {formErrors.lastName && <p className="mt-1 text-sm text-red-400">{formErrors.lastName}</p>}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="private-email" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        E-Mail-Adresse <span className="text-gray-500">(optional)</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Mail className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="private-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder="ihre@email.de"
                          value={privateData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          disabled={isLoading}
                          tabIndex={3}
                          className={formErrors.email ? inputErrorClass : inputNormalClass}
                        />
                      </div>
                      {formErrors.email && <p className="mt-1 text-sm text-red-400">{formErrors.email}</p>}
                      <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-status-warning/10 border border-amber-500/30">
                        <AlertTriangle className="h-4 w-4 text-status-warning shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-700 dark:text-amber-200">
                          Ohne E-Mail-Adresse kann das Passwort bei Verlust nicht wiederhergestellt werden.
                        </span>
                      </div>
                    </div>

                    {/* Confirm Email - sanfte Animation beim Einblenden */}
                    <div
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: privateData.email.trim() !== '' ? '150px' : '0',
                        opacity: privateData.email.trim() !== '' ? 1 : 0,
                      }}
                    >
                      <div className="pt-1">
                        <label htmlFor="private-confirmEmail" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                          E-Mail-Adresse bestätigen *
                        </label>
                        <div className="mt-2 relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Mail className="h-5 w-5 text-gray-500" />
                          </div>
                          <input
                            id="private-confirmEmail"
                            name="confirmEmail"
                            type="email"
                            autoComplete="off"
                            placeholder="ihre@email.de"
                            value={privateData.confirmEmail || ''}
                            onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                            onPaste={(e) => e.preventDefault()}
                            disabled={isLoading}
                            tabIndex={4}
                            className={formErrors.confirmEmail ? inputErrorClass : inputNormalClass}
                          />
                        </div>
                        {formErrors.confirmEmail && <p className="mt-1 text-sm text-red-400">{formErrors.confirmEmail}</p>}
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label htmlFor="private-username" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Benutzername * <span className="text-gray-500">(mindestens 3 Zeichen)</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <UserCheck className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="private-username"
                          type="text"
                          placeholder="benutzername"
                          value={privateData.username}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          disabled={isLoading}
                          tabIndex={5}
                          className={formErrors.username ? inputErrorClass : inputNormalClass}
                        />
                      </div>
                      {formErrors.username && <p className="mt-1 text-sm text-red-400">{formErrors.username}</p>}
                    </div>
                  </div>
                )}

                {/* Step 2 (Company): Company Data */}
                {currentStep === 2 && accountType === 'company' && (
                  <div className="space-y-5">
                    {/* Company Name */}
                    <div>
                      <label htmlFor="company-companyName" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Firmenname *
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Building className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="company-companyName"
                          type="text"
                          placeholder="Musterfirma GmbH"
                          value={companyData.companyName}
                          onChange={(e) => handleInputChange('companyName', e.target.value)}
                          disabled={isLoading}
                          tabIndex={1}
                          data-autofocus
                          className={formErrors.companyName ? inputErrorClass : inputNormalClass}
                        />
                      </div>
                      {formErrors.companyName && <p className="mt-1 text-sm text-red-400">{formErrors.companyName}</p>}
                    </div>

                    {/* Address Section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-100">
                        <Building className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        Firmenadresse *
                      </div>

                      {/* Street + House Number */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <input
                            id="company-street"
                            placeholder="Straße"
                            value={companyData.address.street}
                            onChange={(e) => handleInputChange('street', e.target.value, true)}
                            disabled={isLoading}
                            tabIndex={2}
                            className={formErrors.street ? inputNoIconErrorClass : inputNoIconClass}
                          />
                          {formErrors.street && <p className="mt-1 text-sm text-red-400">{formErrors.street}</p>}
                        </div>
                        <div>
                          <input
                            id="company-houseNumber"
                            placeholder="Nr."
                            value={companyData.address.houseNumber}
                            onChange={(e) => handleInputChange('houseNumber', e.target.value, true)}
                            disabled={isLoading}
                            tabIndex={3}
                            className={formErrors.houseNumber ? inputNoIconErrorClass : inputNoIconClass}
                          />
                          {formErrors.houseNumber && <p className="mt-1 text-sm text-red-400">{formErrors.houseNumber}</p>}
                        </div>
                      </div>

                      {/* Zip + City */}
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <input
                            id="company-zipCode"
                            placeholder="PLZ"
                            value={companyData.address.zipCode}
                            onChange={(e) => handleInputChange('zipCode', e.target.value, true)}
                            disabled={isLoading}
                            tabIndex={4}
                            className={formErrors.zipCode ? inputNoIconErrorClass : inputNoIconClass}
                          />
                          {formErrors.zipCode && <p className="mt-1 text-sm text-red-400">{formErrors.zipCode}</p>}
                        </div>
                        <div className="col-span-2">
                          <input
                            id="company-city"
                            placeholder="Stadt"
                            value={companyData.address.city}
                            onChange={(e) => handleInputChange('city', e.target.value, true)}
                            disabled={isLoading}
                            tabIndex={5}
                            className={formErrors.city ? inputNoIconErrorClass : inputNoIconClass}
                          />
                          {formErrors.city && <p className="mt-1 text-sm text-red-400">{formErrors.city}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3 (Private): Security */}
                {currentStep === 3 && accountType === 'private' && (
                  <div className="space-y-5">
                    {/* Beta Code */}
                    <div>
                      <label className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Beta-Code <span className="text-gray-500">(optional)</span>
                      </label>
                      <div className="mt-2">
                        <BetaCodeInput
                          id="private-betaCode"
                          value={privateData.betaCode || ''}
                          onChange={(val) => handleInputChange('betaCode', val)}
                          disabled={isLoading}
                          tabIndex={1}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Hast du einen Einladungscode? Gib ihn hier ein für Beta-Zugang.
                      </p>
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="private-password" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Passwort * <span className="text-gray-500">(mindestens 6 Zeichen)</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="private-password"
                          name="new-password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Sicheres Passwort"
                          value={privateData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          disabled={isLoading}
                          tabIndex={2}
                          className={`${formErrors.password ? inputErrorClass : inputNormalClass} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {formErrors.password && <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label htmlFor="private-confirmPassword" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Passwort bestätigen *
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="private-confirmPassword"
                          name="confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Passwort wiederholen"
                          value={privateData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          disabled={isLoading}
                          tabIndex={3}
                          className={`${formErrors.confirmPassword ? inputErrorClass : inputNormalClass} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isLoading}
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {formErrors.confirmPassword && <p className="mt-1 text-sm text-red-400">{formErrors.confirmPassword}</p>}
                    </div>

                    {/* Terms */}
                    <div className="flex items-start gap-3 pt-2">
                      <input
                        id="private-agreeToTerms"
                        type="checkbox"
                        checked={privateData.agreeToTerms}
                        onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                        disabled={isLoading}
                        tabIndex={4}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 accent-brand focus:ring-2 focus:ring-brand/50 focus:ring-offset-0 cursor-pointer"
                      />
                      <label htmlFor="private-agreeToTerms" className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                        Ich stimme den{' '}
                        <a href="/agb" target="_blank" rel="noopener noreferrer" tabIndex={-1} className="text-brand hover:text-brand-hover underline">
                          Nutzungsbedingungen
                        </a>{' '}
                        und der{' '}
                        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" tabIndex={-1} className="text-brand hover:text-brand-hover underline">
                          Datenschutzerklärung
                        </a>{' '}
                        zu. *
                      </label>
                    </div>
                    {formErrors.agreeToTerms && <p className="text-sm text-red-400">{formErrors.agreeToTerms}</p>}
                  </div>
                )}

                {/* Step 3 (Company): Contact Person */}
                {currentStep === 3 && accountType === 'company' && (
                  <div className="space-y-5">
                    {/* Name Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="company-firstName" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                          Vorname *
                        </label>
                        <div className="mt-2 relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <input
                            id="company-firstName"
                            type="text"
                            placeholder="Max"
                            value={companyData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            disabled={isLoading}
                            tabIndex={1}
                            data-autofocus
                            className={formErrors.firstName ? inputErrorClass : inputNormalClass}
                          />
                        </div>
                        {formErrors.firstName && <p className="mt-1 text-sm text-red-400">{formErrors.firstName}</p>}
                      </div>

                      <div>
                        <label htmlFor="company-lastName" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                          Nachname *
                        </label>
                        <div className="mt-2 relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                          <input
                            id="company-lastName"
                            type="text"
                            placeholder="Mustermann"
                            value={companyData.lastName}
                            onChange={(e) => handleInputChange('lastName', e.target.value)}
                            disabled={isLoading}
                            tabIndex={2}
                            className={formErrors.lastName ? inputErrorClass : inputNormalClass}
                          />
                        </div>
                        {formErrors.lastName && <p className="mt-1 text-sm text-red-400">{formErrors.lastName}</p>}
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="company-email" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        E-Mail-Adresse <span className="text-gray-500">(optional)</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Mail className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="company-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          placeholder="ihre@firma.de"
                          value={companyData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          disabled={isLoading}
                          tabIndex={3}
                          className={formErrors.email ? inputErrorClass : inputNormalClass}
                        />
                      </div>
                      {formErrors.email && <p className="mt-1 text-sm text-red-400">{formErrors.email}</p>}
                      <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-status-warning/10 border border-amber-500/30">
                        <AlertTriangle className="h-4 w-4 text-status-warning shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-700 dark:text-amber-200">
                          Ohne E-Mail-Adresse kann das Passwort bei Verlust nicht wiederhergestellt werden.
                        </span>
                      </div>
                    </div>

                    {/* Confirm Email - sanfte Animation beim Einblenden */}
                    <div
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: companyData.email.trim() !== '' ? '150px' : '0',
                        opacity: companyData.email.trim() !== '' ? 1 : 0,
                      }}
                    >
                      <div className="pt-1">
                        <label htmlFor="company-confirmEmail" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                          E-Mail-Adresse bestätigen *
                        </label>
                        <div className="mt-2 relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <Mail className="h-5 w-5 text-gray-500" />
                          </div>
                          <input
                            id="company-confirmEmail"
                            name="confirmEmail"
                            type="email"
                            autoComplete="off"
                            placeholder="ihre@firma.de"
                            value={companyData.confirmEmail || ''}
                            onChange={(e) => handleInputChange('confirmEmail', e.target.value)}
                            onPaste={(e) => e.preventDefault()}
                            disabled={isLoading}
                            tabIndex={4}
                            className={formErrors.confirmEmail ? inputErrorClass : inputNormalClass}
                          />
                        </div>
                        {formErrors.confirmEmail && <p className="mt-1 text-sm text-red-400">{formErrors.confirmEmail}</p>}
                      </div>
                    </div>

                    {/* Username */}
                    <div>
                      <label htmlFor="company-username" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Benutzername * <span className="text-gray-500">(mindestens 3 Zeichen)</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <UserCheck className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="company-username"
                          type="text"
                          placeholder="benutzername"
                          value={companyData.username}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          disabled={isLoading}
                          tabIndex={5}
                          className={formErrors.username ? inputErrorClass : inputNormalClass}
                        />
                      </div>
                      {formErrors.username && <p className="mt-1 text-sm text-red-400">{formErrors.username}</p>}
                    </div>
                  </div>
                )}

                {/* Step 4 (Company): Security */}
                {currentStep === 4 && accountType === 'company' && (
                  <div className="space-y-5">
                    {/* Beta Code */}
                    <div>
                      <label className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Beta-Code <span className="text-gray-500">(optional)</span>
                      </label>
                      <div className="mt-2">
                        <BetaCodeInput
                          id="company-betaCode"
                          value={companyData.betaCode || ''}
                          onChange={(val) => handleInputChange('betaCode', val)}
                          disabled={isLoading}
                          tabIndex={1}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Hast du einen Einladungscode? Gib ihn hier ein für Beta-Zugang.
                      </p>
                    </div>

                    {/* Password */}
                    <div>
                      <label htmlFor="company-password" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Passwort * <span className="text-gray-500">(mindestens 6 Zeichen)</span>
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="company-password"
                          name="new-password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Sicheres Passwort"
                          value={companyData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          disabled={isLoading}
                          tabIndex={2}
                          className={`${formErrors.password ? inputErrorClass : inputNormalClass} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {formErrors.password && <p className="mt-1 text-sm text-red-400">{formErrors.password}</p>}
                    </div>

                    {/* Confirm Password */}
                    <div>
                      <label htmlFor="company-confirmPassword" className="block text-sm/6 font-medium text-gray-700 dark:text-gray-100">
                        Passwort bestätigen *
                      </label>
                      <div className="mt-2 relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <Lock className="h-5 w-5 text-gray-500" />
                        </div>
                        <input
                          id="company-confirmPassword"
                          name="confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder="Passwort wiederholen"
                          value={companyData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          disabled={isLoading}
                          tabIndex={3}
                          className={`${formErrors.confirmPassword ? inputErrorClass : inputNormalClass} pr-10`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isLoading}
                          tabIndex={-1}
                          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {formErrors.confirmPassword && <p className="mt-1 text-sm text-red-400">{formErrors.confirmPassword}</p>}
                    </div>

                    {/* Terms */}
                    <div className="flex items-start gap-3 pt-2">
                      <input
                        id="company-agreeToTerms"
                        type="checkbox"
                        checked={companyData.agreeToTerms}
                        onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                        disabled={isLoading}
                        tabIndex={4}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 accent-teal-500 focus:ring-2 focus:ring-teal-500/50 focus:ring-offset-0 cursor-pointer"
                      />
                      <label htmlFor="company-agreeToTerms" className="text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                        Ich stimme den{' '}
                        <a href="/agb" target="_blank" rel="noopener noreferrer" tabIndex={-1} className="text-teal-400 hover:text-teal-300 underline">
                          Nutzungsbedingungen
                        </a>{' '}
                        und der{' '}
                        <a href="/datenschutz" target="_blank" rel="noopener noreferrer" tabIndex={-1} className="text-teal-400 hover:text-teal-300 underline">
                          Datenschutzerklärung
                        </a>{' '}
                        zu. *
                      </label>
                    </div>
                    {formErrors.agreeToTerms && <p className="text-sm text-red-400">{formErrors.agreeToTerms}</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {!registrationSuccess && (
            <div className="flex flex-row-reverse gap-3 pt-2">
              {/* Next/Submit Button */}
              {isLastStep ? (
                <button
                  type="submit"
                  disabled={isLoading || !isCurrentStepValid()}
                  tabIndex={10}
                  className={primaryButtonClass}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Wird registriert...
                    </>
                  ) : (
                    <>
                      {accountType === 'private' ? <Users className="h-5 w-5" /> : <Briefcase className="h-5 w-5" />}
                      {accountType === 'private' ? 'Privatkonto erstellen' : 'Firmenkonto erstellen'}
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid()}
                  tabIndex={10}
                  className={primaryButtonClass}
                >
                  Weiter
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}

              {/* Back Button */}
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isLoading}
                  tabIndex={11}
                  className={secondaryButtonClass}
                >
                  <ChevronLeft className="h-5 w-5" />
                  Zurück
                </button>
              )}
            </div>
          )}

          </form>
        </div>

        {/* Switch to Login */}
        <p className="mt-6 text-center text-sm/6 text-gray-500 dark:text-gray-400">
          Bereits ein Konto?{' '}
          {onSwitchToLogin ? (
            <button
              type="button"
              onClick={onSwitchToLogin}
              disabled={isLoading}
              tabIndex={12}
              className="text-brand hover:text-brand-hover transition-colors"
            >
              Jetzt anmelden
            </button>
          ) : (
            <Link
              to="/login"
              tabIndex={12}
              className="text-brand hover:text-brand-hover transition-colors"
            >
              Jetzt anmelden
            </Link>
          )}
        </p>
      </div>
    </div>
  );
};

export default RegisterFormTW;
